/**
 * Color Utility Functions
 * Validates, converts, and generates color palettes dynamically
 */

/**
 * Validates if a string is a valid hex color
 * @param color - Color string to validate (e.g., "#ff9500", "ff9500")
 * @returns true if valid hex color, false otherwise
 */
export function isValidHexColor(color: string): boolean {
  if (!color || typeof color !== "string") {
    return false;
  }

  // Remove leading # if present
  const hex = color.startsWith("#") ? color.slice(1) : color;

  // Check if it's a valid 3 or 6 character hex string
  return /^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
}

/**
 * Converts hex color to RGB
 * @param hex - Hex color string (e.g., "#ff9500")
 * @returns Object with r, g, b properties (0-255)
 */
export function hexToRgb(hex: string): {
  r: number;
  g: number;
  b: number;
} {
  const cleaned = hex.startsWith("#") ? hex.slice(1) : hex;
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleaned);

  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Converts RGB to Hex color
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Hex color string (e.g., "#ff9500")
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

/**
 * Converts RGB to HSL
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Object with h (0-360), s (0-100), l (0-100) properties
 */
export function rgbToHsl(
  r: number,
  g: number,
  b: number,
): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Converts HSL to RGB
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Object with r, g, b properties (0-255)
 */
export function hslToRgb(
  h: number,
  s: number,
  l: number,
): { r: number; g: number; b: number } {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Generates a color palette with 10 shades from a base color
 * Using HSL adjustments for perceptually uniform results
 * Palette includes: 50, 100, 200, 300, 400, 500 (base), 600, 700, 800, 900
 *
 * @param baseColor - Hex color string (e.g., "#ff9500")
 * @returns Object with keys "50"-"900" and hex color values
 */
export function generateColorPalette(
  baseColor: string,
): Record<string, string> {
  if (!isValidHexColor(baseColor)) {
    console.warn(`Invalid color for palette generation: ${baseColor}`);
    return getDefaultPalette();
  }

  const rgb = hexToRgb(baseColor);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Define lightness values for each shade
  // The base color (500) keeps its original lightness
  const lightnessMap: Record<string, number> = {
    "50": Math.min(hsl.l + 40, 95), // Very light
    "100": Math.min(hsl.l + 35, 90),
    "200": Math.min(hsl.l + 28, 85),
    "300": Math.min(hsl.l + 20, 80),
    "400": Math.min(hsl.l + 10, 75),
    "500": hsl.l, // Base (original)
    "600": Math.max(hsl.l - 10, 30),
    "700": Math.max(hsl.l - 18, 25),
    "800": Math.max(hsl.l - 28, 20),
    "900": Math.max(hsl.l - 38, 10), // Very dark
  };

  const palette: Record<string, string> = {};

  Object.entries(lightnessMap).forEach(([shade, lightness]) => {
    const newRgb = hslToRgb(hsl.h, hsl.s, lightness);
    palette[shade] = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  });

  return palette;
}

/**
 * Gets the default orange palette (current MaxMonitor brand color)
 * @returns Default color palette as fallback
 */
export function getDefaultPalette(): Record<string, string> {
  return {
    "50": "#fff8e6",
    "100": "#ffeab3",
    "200": "#ffdc80",
    "300": "#ffce4d",
    "400": "#ffc01a",
    "500": "#ff9500",
    "600": "#e6800a",
    "700": "#cc6b14",
    "800": "#b3561e",
    "900": "#994128",
  };
}

/**
 * Applies a color palette to CSS variables on the root element
 * Updates: --color-primary-*, --color-warning-*, --color-info-*
 * Also updates: --color-chart-performance, --color-chart-qualidade, --color-chart-oee
 *
 * @param palette - Color palette object (keys "50"-"900")
 * @returns true on success, false on error
 */
export function applyColorPaletteToDom(
  palette: Record<string, string>,
): boolean {
  try {
    const root = document.documentElement;

    // Apply primary, warning, and info colors
    ["primary", "warning", "info"].forEach((colorType) => {
      Object.entries(palette).forEach(([shade, color]) => {
        root.style.setProperty(`--color-${colorType}-${shade}`, color);
      });
    });

    // Apply chart colors (all use the base color 500)
    const baseColor = palette["500"];
    root.style.setProperty("--color-chart-performance", baseColor);

    // For qualidade, use a slightly lighter shade (400)
    const qualidadeColor = palette["400"] || baseColor;
    root.style.setProperty("--color-chart-qualidade", qualidadeColor);

    // For OEE, use a slightly darker shade (600)
    const oeeColor = palette["600"] || baseColor;
    root.style.setProperty("--color-chart-oee", oeeColor);
    
    return true;
  } catch (error) {
    console.error("❌ Failed to apply color palette to DOM:", error);
    return false;
  }
}
